// QRCodeGenerator4.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ColorPickerField from '../../components/ColorPickerField';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Checkbox,
    useMediaQuery,
    useTheme,
    Divider,
    alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PaletteIcon from "@mui/icons-material/Palette";
import StyleIcon from "@mui/icons-material/Style";
import ImageIcon from "@mui/icons-material/Image";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import LinkIcon from "@mui/icons-material/Link";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import SmsIcon from "@mui/icons-material/Sms";
import WifiIcon from "@mui/icons-material/Wifi";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ChatIcon from "@mui/icons-material/Chat";
import AppsIcon from "@mui/icons-material/Apps";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import PublicIcon from "@mui/icons-material/Public";
import QRCodeStyling from "qr-code-styling";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Link as RouterLink, useParams } from "react-router-dom";
import { API_URL, buildFetchUrl } from "../../config";
import { resizeLogoFile, resizeLogoSource } from "./qrLogoUtils";

const Card = ({ sx, ...props }) => (
    <Paper
        elevation={0}
        sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            p: { xs: 1.25, sm: 3 },
            borderRadius: { xs: 2.5, sm: 3.5 },
            border: "1px solid rgba(99,102,241,0.16)",
            background: "linear-gradient(152deg, rgba(255,255,255,0.95) 0%, rgba(248,250,255,0.92) 100%)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 24px 60px rgba(67,56,202,0.12)",
            ...sx,
        }}
        {...props}
    />
);

const sectionTitleSx = {
    fontWeight: 900,
    letterSpacing: "-0.01em",
    color: "#0f172a",
};

const SOCIAL_SERVICE_OPTIONS = [
    { value: "instagram", label: "Instagram", base: "https://www.instagram.com/" },
    { value: "facebook", label: "Facebook", base: "https://www.facebook.com/" },
    { value: "linkedin", label: "LinkedIn", base: "https://www.linkedin.com/in/" },
    { value: "tiktok", label: "TikTok", base: "https://www.tiktok.com/@" },
    { value: "x", label: "X (Twitter)", base: "https://x.com/" },
    { value: "youtube", label: "YouTube", base: "https://www.youtube.com/@" },
    { value: "telegram", label: "Telegram", base: "https://t.me/" },
    { value: "pinterest", label: "Pinterest", base: "https://www.pinterest.com/" },
];

const HIGHEST_ERROR_CORRECTION_LEVEL = "H";
const HIGHEST_EXPORT_QUALITY = 4;

const defaultQrSettings = {
    contentType: "url",
    contentValue: "",
    qrShape: "square",
    dotsColor: "#111827",
    dotsSecondaryColor: "#4f46e5",
    useDotsGradient: false,
    dotsGradientType: "linear",
    dotsGradientRotation: 0,
    bgColor: "#ffffff",
    bgSecondaryColor: "#e0e7ff",
    useBgGradient: false,
    bgGradientType: "linear",
    bgGradientRotation: 0,
    cornerSquareType: "square",
    cornerSquareColor: "#111827",
    cornerDotType: "square",
    cornerDotColor: "#111827",
    border: { width: 1, color: "#111827" },
    frame: { radius: 10, shadow: 0 },
    margin: { h: 5, v: 5 },
    imageOptions: { size: 0.4, margin: 4, hideBackgroundDots: true },
    qrOptions: { errorCorrectionLevel: HIGHEST_ERROR_CORRECTION_LEVEL },
    scanMe: {
        enabled: true,
        text: "SCAN ME",
        position: "bottom",
        fontSize: 16,
        fontWeight: 900,
        letterSpacing: 1,
        fontColor: "#ffffff",
        borderColor: "#111827",
    },
    quality: HIGHEST_EXPORT_QUALITY,
    logoData: null,
};

const normalizeQrSettings = (settings = {}) => ({
    ...defaultQrSettings,
    ...settings,
    border: {
        ...defaultQrSettings.border,
        ...(settings.border || {}),
    },
    margin: {
        ...defaultQrSettings.margin,
        ...(settings.margin || {}),
    },
    frame: {
        ...defaultQrSettings.frame,
        ...(settings.frame || {}),
    },
    imageOptions: {
        ...defaultQrSettings.imageOptions,
        ...(settings.imageOptions || {}),
    },
    qrOptions: {
        ...defaultQrSettings.qrOptions,
        ...(settings.qrOptions || {}),
        errorCorrectionLevel: HIGHEST_ERROR_CORRECTION_LEVEL,
    },
    scanMe: {
        ...defaultQrSettings.scanMe,
        ...(settings.scanMe || {}),
    },
});

export default function QRCodeGenerator4() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
    const isXs = useMediaQuery(theme.breakpoints.only("xs"));
    const isSm = useMediaQuery(theme.breakpoints.only("sm"));
    const qrSize = isXs ? 170 : isSm ? 230 : 320;
    const accordionSx = {
        border: `1px solid ${alpha("#6366f1", 0.18)}`,
        borderRadius: 2.5,
        overflow: "hidden",
        backgroundColor: alpha("#ffffff", 0.94),
        "&:before": { display: "none" },
        boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.8)}, 0 10px 22px ${alpha("#4338ca", 0.08)}`,
        transition: "all .25s ease",
        "& .MuiAccordionSummary-root": { minHeight: 58, px: { xs: 1.25, sm: 2.25 } },
        "& .MuiAccordionSummary-content": { my: 1.2, minWidth: 0, overflowWrap: "anywhere" },
        "& .MuiAccordionSummary-content .MuiTypography-root": { overflowWrap: "anywhere", wordBreak: "break-word" },
        "& .MuiAccordionDetails-root": { px: { xs: 1.25, sm: 2.5 }, pb: 2.4, minWidth: 0, overflow: "hidden" },
        "&:hover": {
            borderColor: alpha("#6366f1", 0.35),
            transform: "translateY(-1px)",
        },
    };

    const premiumInputSx = {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            background: "linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(241,245,255,0.95) 100%)",
            boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.92)}`,
            transition: "all .2s ease",
            minWidth: 0,
            maxWidth: "100%",
            "& fieldset": { borderColor: alpha("#6366f1", 0.2) },
            "&:hover fieldset": { borderColor: alpha("#6366f1", 0.45) },
            "&.Mui-focused": {
                boxShadow: `0 0 0 4px ${alpha("#6366f1", 0.14)}`,
            },
        },
    };

    const mobileSafeFormSx = {
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",

        "& .MuiGrid-root": {
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
        },

        "& .MuiGrid-item": {
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
        },

        "& .MuiStack-root": {
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
        },

        "& .MuiFormControl-root, & .MuiTextField-root": {
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
        },

        "& .MuiInputBase-root": {
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
        },

        "& .MuiSelect-select, & input, & textarea": {
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },

        "& .MuiFormControlLabel-root": {
            maxWidth: "100%",
            minWidth: 0,
            mr: 0,
            alignItems: "flex-start",
        },

        "& .MuiFormControlLabel-label": {
            minWidth: 0,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
        },
    };

    const qrCodeRef = useRef(null);
    const previewRef = useRef(null);
    const downloadRef = useRef(null);

    const { id } = useParams();

    // Core state
    const [contentType, setContentType] = useState("url");
    const [contentValue, setContentValue] = useState("https://example.com");
    const [vcard, setVcard] = useState({ name: "", phone: "", email: "" });
    const [emailDetails, setEmailDetails] = useState({ to: "", subject: "", body: "" });
    const [smsDetails, setSmsDetails] = useState({ phone: "", message: "" });
    const [wifiDetails, setWifiDetails] = useState({ ssid: "", password: "", encryption: "WPA", hidden: false });
    const [locationDetails, setLocationDetails] = useState({ latitude: "", longitude: "", label: "" });
    const [eventDetails, setEventDetails] = useState({
        title: "",
        location: "",
        start: "",
        end: "",
        description: "",
    });
    const [chatDetails, setChatDetails] = useState({ handle: "", message: "" });
    const [appDetails, setAppDetails] = useState({ ios: "", android: "", fallback: "" });
    const [bitcoinDetails, setBitcoinDetails] = useState({ wallet: "", amount: "", label: "", message: "" });
    const [faceTimeDetails, setFaceTimeDetails] = useState({ handle: "" });
    const [socialDetails, setSocialDetails] = useState({ service: "instagram", handle: "", customUrl: "" });

    const [qrShape, setQrShape] = useState("square");
    const [cornerSquareType, setCornerSquareType] = useState("square");
    const [cornerSquareColor, setCornerSquareColor] = useState("#111827");
    const [cornerDotType, setCornerDotType] = useState("square");
    const [cornerDotColor, setCornerDotColor] = useState("#111827");
    const [showScanMe, setShowScanMe] = useState(true);
    const [ScanMe, setScanMe] = useState("SCAN ME");
    const [scanMePosition, setScanMePosition] = useState("bottom");
    const [scanMeFontSize, setScanMeFontSize] = useState(16);
    const [scanMeFontWeight, setScanMeFontWeight] = useState(900);
    const [scanMeLetterSpacing, setScanMeLetterSpacing] = useState(1);
    const [scanMeFontColor, setScanMeFontColor] = useState("#ffffff");
    const [scanMeBorderColor, setScanMeBorderColor] = useState("#111827");

    const [borderWidth, setBorderWidth] = useState(defaultQrSettings.border.width);
    const [borderColor, setBorderColor] = useState("#111827");
    const [frameRadius, setFrameRadius] = useState(defaultQrSettings.frame.radius);
    const [frameShadow, setFrameShadow] = useState(defaultQrSettings.frame.shadow);
    const [marginH, setMarginH] = useState(defaultQrSettings.margin.h);
    const [marginV, setMarginV] = useState(defaultQrSettings.margin.v);
    const [dotsColor, setDotsColor] = useState("#111827");
    const [dotsSecondaryColor, setDotsSecondaryColor] = useState("#4f46e5");
    const [useDotsGradient, setUseDotsGradient] = useState(false);
    const [dotsGradientType, setDotsGradientType] = useState("linear");
    const [dotsGradientRotation, setDotsGradientRotation] = useState(0);
    const [bgColor, setBgColor] = useState("#ffffff");
    const [bgSecondaryColor, setBgSecondaryColor] = useState("#e0e7ff");
    const [useBgGradient, setUseBgGradient] = useState(false);
    const [bgGradientType, setBgGradientType] = useState("linear");
    const [bgGradientRotation, setBgGradientRotation] = useState(0);
    const errorCorrectionLevel = HIGHEST_ERROR_CORRECTION_LEVEL;

    const qrFrameWidth = qrSize + marginH * 2;
    const qrFrameHeight = qrSize + marginV * 2;
    const safePreviewWidth = `min(100%, ${qrFrameWidth}px)`;
    const frameFillColor = showScanMe ? scanMeBorderColor : borderColor;

    const quality = HIGHEST_EXPORT_QUALITY;
    const [logoData, setLogoData] = useState(null);
    const [logoSize, setLogoSize] = useState(0.4);
    const [logoMargin, setLogoMargin] = useState(4);
    const [hideLogoBgDots, setHideLogoBgDots] = useState(true);
    // const debouncedLogoSize = useDebouncedValue(logoSize, 220);
    // const debouncedLogoMargin = useDebouncedValue(logoMargin, 220);

    const [loading, setLoading] = useState(!!id);
    const [error, setError] = useState("");
    const [recordName, setRecordName] = useState("");
    const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));

    // Init QRStyling (static ref)
    const qrCode = useRef(
        new QRCodeStyling({
            width: 300,
            height: 300,
            data: contentValue,
            qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: HIGHEST_ERROR_CORRECTION_LEVEL },
            dotsOptions: { type: qrShape, color: dotsColor },
            cornersSquareOptions: { type: cornerSquareType, color: cornerSquareColor },
            cornersDotOptions: { type: cornerDotType, color: cornerDotColor },
            backgroundOptions: { color: bgColor },
        })
    ).current;

    const recordScanUrl = useMemo(() => {
        if (!id) return "";
        return buildFetchUrl(id);
    }, [id]);

    // Fetch saved settings (if id)
    useEffect(() => {
        const fetchSettings = async () => {
            if (!id) return;
            try {
                const response = await fetch(`${API_URL}get_qr_settings.php?id=${id}`, {
                    method: "GET",
                    credentials: "include",
                });
                const data = await response.json();
                const normalizedSettings = normalizeQrSettings(data);

                if (data.exists) {
                    setContentType(normalizedSettings.contentType || "url");
                    setContentValue(normalizedSettings.contentValue || "");
                    setQrShape(normalizedSettings.qrShape);
                    setDotsColor(normalizedSettings.dotsColor);
                    setDotsSecondaryColor(normalizedSettings.dotsSecondaryColor);
                    setUseDotsGradient(normalizedSettings.useDotsGradient);
                    setDotsGradientType(normalizedSettings.dotsGradientType);
                    setDotsGradientRotation(normalizedSettings.dotsGradientRotation);
                    setBgColor(normalizedSettings.bgColor);
                    setBgSecondaryColor(normalizedSettings.bgSecondaryColor);
                    setUseBgGradient(normalizedSettings.useBgGradient);
                    setBgGradientType(normalizedSettings.bgGradientType);
                    setBgGradientRotation(normalizedSettings.bgGradientRotation);
                    setCornerSquareType(normalizedSettings.cornerSquareType);
                    setCornerSquareColor(normalizedSettings.cornerSquareColor);
                    setCornerDotType(normalizedSettings.cornerDotType);
                    setCornerDotColor(normalizedSettings.cornerDotColor);
                    setBorderWidth(normalizedSettings.border.width);
                    setBorderColor(normalizedSettings.border.color);
                    setFrameRadius(normalizedSettings.frame.radius);
                    setFrameShadow(normalizedSettings.frame.shadow);
                    setMarginH(normalizedSettings.margin.h);
                    setMarginV(normalizedSettings.margin.v);
                    setShowScanMe(normalizedSettings.scanMe.enabled);
                    setScanMe(normalizedSettings.scanMe.text);
                    setScanMePosition(normalizedSettings.scanMe.position);
                    setScanMeFontSize(normalizedSettings.scanMe.fontSize);
                    setScanMeFontWeight(normalizedSettings.scanMe.fontWeight);
                    setScanMeLetterSpacing(normalizedSettings.scanMe.letterSpacing);
                    setScanMeFontColor(normalizedSettings.scanMe.fontColor);
                    setScanMeBorderColor(normalizedSettings.scanMe.borderColor);
                    if (normalizedSettings.logoData) {
                        resizeLogoSource(normalizedSettings.logoData)
                            .then(setLogoData)
                            .catch(() => setLogoData(normalizedSettings.logoData));
                    } else {
                        setLogoData(null);
                    }
                    setLogoSize(normalizedSettings.imageOptions.size);
                    setLogoMargin(normalizedSettings.imageOptions.margin);
                    setHideLogoBgDots(normalizedSettings.imageOptions.hideBackgroundDots);
                }
            } catch {
                setError("Failed to load settings");
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const fetchRecordName = async () => {
            if (!id) return;
            try {
                const response = await fetch(`${API_URL}get_restaurant.php?id=${id}`, {
                    method: "GET",
                    credentials: "include",
                });
                const data = await response.json();
                if (response.ok) {
                    setRecordName(data.name || "");
                }
            } catch (recordError) {
                console.error("Failed to load record name", recordError);
            }
        };

        fetchRecordName();
    }, [id]);

    // Build data string
    const qrData = useMemo(() => {
        if (id) return recordScanUrl;
        if (contentType === "url") return contentValue.trim();
        if (contentType === "email") {
            const query = new URLSearchParams();
            if (emailDetails.subject) query.set("subject", emailDetails.subject);
            if (emailDetails.body) query.set("body", emailDetails.body);
            const suffix = query.toString() ? `?${query.toString()}` : "";
            return `mailto:${emailDetails.to}${suffix}`;
        }
        if (contentType === "phone") return `tel:${contentValue}`;
        if (contentType === "sms") {
            const encodedMessage = encodeURIComponent(smsDetails.message || "");
            return `SMSTO:${smsDetails.phone}:${encodedMessage}`;
        }
        if (contentType === "vcard") {
            return `BEGIN:VCARD
VERSION:3.0
FN:${vcard.name}
TEL:${vcard.phone}
EMAIL:${vcard.email}
END:VCARD`;
        }
        if (contentType === "wifi") {
            const hidden = wifiDetails.hidden ? "H:true;" : "";
            return `WIFI:T:${wifiDetails.encryption};S:${wifiDetails.ssid};P:${wifiDetails.password};${hidden};`;
        }
        if (contentType === "location") {
            return `geo:${locationDetails.latitude},${locationDetails.longitude}?q=${encodeURIComponent(locationDetails.label || "")}`;
        }
        if (contentType === "event") {
            const start = eventDetails.start ? `${eventDetails.start.replace(/[-:]/g, "")}00Z` : "";
            const end = eventDetails.end ? `${eventDetails.end.replace(/[-:]/g, "")}00Z` : "";
            return `BEGIN:VEVENT
SUMMARY:${eventDetails.title}
LOCATION:${eventDetails.location}
DESCRIPTION:${eventDetails.description}
DTSTART:${start}
DTEND:${end}
END:VEVENT`;
        }
        if (contentType === "chat") return `https://wa.me/${chatDetails.handle}?text=${encodeURIComponent(chatDetails.message || "")}`;
        if (contentType === "app") {
            return JSON.stringify({
                ios: appDetails.ios,
                android: appDetails.android,
                fallback: appDetails.fallback,
            });
        }
        if (contentType === "bitcoin") {
            const query = new URLSearchParams();
            if (bitcoinDetails.amount) query.set("amount", bitcoinDetails.amount);
            if (bitcoinDetails.label) query.set("label", bitcoinDetails.label);
            if (bitcoinDetails.message) query.set("message", bitcoinDetails.message);
            return `bitcoin:${bitcoinDetails.wallet}${query.toString() ? `?${query.toString()}` : ""}`;
        }
        if (contentType === "facetime") return `facetime:${faceTimeDetails.handle}`;
        if (contentType === "social") {
            if (socialDetails.customUrl) return socialDetails.customUrl.trim();
            const selected = SOCIAL_SERVICE_OPTIONS.find((item) => item.value === socialDetails.service);
            return `${selected?.base || "https://"}${(socialDetails.handle || "").replace(/^\/+/, "")}`;
        }
        return contentValue;
    }, [contentType, contentValue, vcard, id, recordScanUrl, emailDetails, smsDetails, wifiDetails, locationDetails, eventDetails, chatDetails, appDetails, bitcoinDetails, faceTimeDetails, socialDetails]);

    // Update QR
    useEffect(() => {
        if (loading || !qrCodeRef.current) return;

        // qr-code-styling merges update options, so explicitly clear gradients
        // when checkboxes are turned off instead of omitting the gradient key.
        qrCode.update({
            width: qrSize,
            height: qrSize,
            data: qrData,
            qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel },
            dotsOptions: useDotsGradient
                ? {
                    type: qrShape,
                    color: dotsColor,
                    gradient: {
                        type: dotsGradientType,
                        rotation: (dotsGradientRotation * Math.PI) / 180,
                        colorStops: [
                            { offset: 0, color: dotsColor },
                            { offset: 1, color: dotsSecondaryColor },
                        ],
                    },
                }
                : { type: qrShape, color: dotsColor, gradient: null },
            cornersSquareOptions: { type: cornerSquareType, color: cornerSquareColor },
            cornersDotOptions: { type: cornerDotType, color: cornerDotColor },
            backgroundOptions: useBgGradient
                ? {
                    color: bgColor,
                    gradient: {
                        type: bgGradientType,
                        rotation: (bgGradientRotation * Math.PI) / 180,
                        colorStops: [
                            { offset: 0, color: bgColor },
                            { offset: 1, color: bgSecondaryColor },
                        ],
                    },
                }
                : { color: bgColor, gradient: null },
        });

        if (!qrCodeRef.current.hasChildNodes()) {
            qrCode.append(qrCodeRef.current);
        }
    }, [loading, qrData, qrShape, dotsColor, bgColor, qrSize, qrCode, useDotsGradient, dotsSecondaryColor, dotsGradientType, dotsGradientRotation, cornerSquareType, cornerSquareColor, cornerDotType, cornerDotColor, useBgGradient, bgSecondaryColor, bgGradientType, bgGradientRotation, errorCorrectionLevel]);

    // Upload logo
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            alert("Logo size should be less than 2MB");
            e.target.value = "";
            return;
        }

        try {
            const optimizedLogo = await resizeLogoFile(file);
            setLogoData(optimizedLogo);
        } catch (logoError) {
            alert(logoError.message || "Unable to optimize this logo. Please try a different image.");
        } finally {
            e.target.value = "";
        }
    };

    // Download helpers
    const handleDownload = async (ext) => {
        if (ext === "svg") {
            qrCode.download({ extension: "svg", name: "qr-code" });
            return;
        }
        const scaleFactor = (window.devicePixelRatio || 2) * quality;
        // const canvas = await html2canvas(downloadRef.current, {
        //     backgroundColor: null,
        //     scale: scaleFactor,
        // });

        const canvas = await html2canvas(downloadRef.current, {
            backgroundColor: null,
            scale: scaleFactor,
            onclone: (clonedDoc) => {
                const scanText = clonedDoc.querySelector('[data-export-scan-text="true"]');

                if (scanText) {
                    // scanText.style.transform = "translateY(-6px)";
                    const exportOffset = Math.max(4, Math.round(scanMeFontSize * 0.28));
                    scanText.style.transform = `translateY(-${exportOffset}px)`;
                    scanText.style.display = "block";
                }
            },
        });

        if (ext === "pdf") {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({ unit: "pt", format: "a4" });
            // center on page
            const pageW = pdf.internal.pageSize.getWidth();
            const imgW = Math.min(pageW - 72, canvas.width); // margins
            const ratio = imgW / canvas.width;
            const imgH = canvas.height * ratio;
            pdf.addImage(imgData, "PNG", (pageW - imgW) / 2, 72, imgW, imgH);
            pdf.save("qr-code.pdf");
        } else {
            canvas.toBlob(
                (blob) => {
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `qr-code.${ext}`;
                    link.click();
                },
                ext === "jpeg" ? "image/jpeg" : "image/png",
                1
            );
        }
    };

    // Save settings
    const buildQrSettings = () => ({
        contentType,
        contentValue,
        ...(!id && { vcard }),
        ...(!id && { emailDetails, smsDetails, wifiDetails, locationDetails, eventDetails, chatDetails, appDetails }),
        qrShape,
        dotsColor,
        dotsSecondaryColor,
        useDotsGradient,
        dotsGradientType,
        dotsGradientRotation,
        bgColor,
        bgSecondaryColor,
        useBgGradient,
        bgGradientType,
        bgGradientRotation,
        cornerSquareType,
        cornerSquareColor,
        cornerDotType,
        cornerDotColor,
        border: { width: borderWidth, color: borderColor },
        frame: { radius: frameRadius, shadow: frameShadow },
        margin: { h: marginH, v: marginV },
        imageOptions: { size: logoSize, margin: logoMargin, hideBackgroundDots: hideLogoBgDots },
        qrOptions: { errorCorrectionLevel },
        scanMe: {
            enabled: showScanMe,
            text: ScanMe,
            position: scanMePosition,
            fontSize: scanMeFontSize,
            fontWeight: scanMeFontWeight,
            letterSpacing: scanMeLetterSpacing,
            fontColor: scanMeFontColor,
            borderColor: scanMeBorderColor,
        },
        quality,
        logoData,
    });

    const handleSaveSettings = async () => {
        try {
            if (id) {
                const formData = new FormData();
                formData.append("id", id);
                formData.append("settings", JSON.stringify(buildQrSettings()));
                const response = await fetch(`${API_URL}save_qr_settings.php`, {
                    method: "POST",
                    body: formData,
                    credentials: "include",
                });
                if (!response.ok) throw new Error("Save failed");
            }
            alert("Settings saved successfully");
        } catch (err) {
            console.error(err);
            alert("Error saving settings");
        }
    };

    if (loading) return <Typography sx={{ p: 2 }}>Loading…</Typography>;
    if (error) return <Typography color="error" sx={{ p: 2 }}>{error}</Typography>;

    return (
        <Container
            maxWidth="lg"
            disableGutters
            sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
                boxSizing: "border-box",
                py: { xs: 2, md: 6 },
                px: { xs: 1.25, sm: 2, md: 3 },
            }}
        >
            <Stack spacing={1.1} alignItems="center" textAlign="center" sx={{ mb: { xs: 2.5, md: 4 } }}>
                <Chip
                    label="Studio-grade QR builder"
                    sx={{
                        px: 0.7,
                        fontWeight: 700,
                        color: "primary.main",
                        bgcolor: alpha("#6366f1", 0.1),
                        border: `1px solid ${alpha("#6366f1", 0.25)}`,
                    }}
                />
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.02em", fontSize: { xs: "1.65rem", md: "2.2rem" } }}>
                    {recordName ? `QR for ${recordName}` : "Design, preview, and export in seconds"}
                </Typography>
                {recordName && (
                    <Typography variant="body1" color="text.secondary">
                        You are editing the QR presentation for this record.
                    </Typography>
                )}
            </Stack>
            <Grid
                container
                spacing={{ xs: 0, sm: 3, md: 4 }}
                // Preview first on mobile
                direction={{ xs: "column-reverse", md: "row" }}
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    mx: 0,
                    rowGap: { xs: 2, sm: 0 },
                    overflowX: "hidden",
                    "& > .MuiGrid-item": {
                        minWidth: 0,
                        maxWidth: "100%",
                        boxSizing: "border-box",
                    },
                }}
            >
                {/* LEFT: Controls */}
                <Grid
                    item
                    xs={12}
                    md={7}
                    sx={{
                        minWidth: 0,
                        maxWidth: "100%",
                        width: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Stack spacing={{ xs: 2, sm: 3 }} sx={{ width: "100%", minWidth: 0 }}>
                        <Card sx={mobileSafeFormSx}>
                            <Stack spacing={2}>
                                <Typography variant="h6" sx={sectionTitleSx}>
                                    Content
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Add your destination and start with premium presets.
                                </Typography>

                                {!id && (
                                    <FormControl fullWidth sx={premiumInputSx}>
                                        <InputLabel>Content Type</InputLabel>
                                        <Select
                                            value={contentType}
                                            label="Content Type"
                                            onChange={(e) => setContentType(e.target.value)}
                                        >
                                            <MenuItem value="url">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <LinkIcon fontSize="small" /> <span>URL</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="text">Text</MenuItem>
                                            <MenuItem value="email">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <EmailIcon fontSize="small" /> <span>Email</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="phone">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PhoneIphoneIcon fontSize="small" /> <span>Phone</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="sms">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <SmsIcon fontSize="small" /> <span>SMS</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="vcard">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PersonIcon fontSize="small" /> <span>vCard</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="wifi">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <WifiIcon fontSize="small" /> <span>Wi-Fi</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="location">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <LocationOnIcon fontSize="small" /> <span>Location</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="event">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <EventIcon fontSize="small" /> <span>Event</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="chat">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <ChatIcon fontSize="small" /> <span>Chat Link</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="app">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <AppsIcon fontSize="small" /> <span>App Links</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="social">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <PublicIcon fontSize="small" /> <span>Social Profile</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="bitcoin">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <CurrencyBitcoinIcon fontSize="small" /> <span>Bitcoin Payment</span>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem value="facetime">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <VideoCallIcon fontSize="small" /> <span>FaceTime</span>
                                                </Stack>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                )}

                                {contentType === "vcard" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Full Name"
                                                value={vcard.name}
                                                onChange={(e) => setVcard({ ...vcard, name: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Phone"
                                                value={vcard.phone}
                                                onChange={(e) => setVcard({ ...vcard, phone: e.target.value })}
                                                InputProps={{ startAdornment: <InputAdornment position="start">+ </InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Email"
                                                value={vcard.email}
                                                onChange={(e) => setVcard({ ...vcard, email: e.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "email" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Recipient Email" value={emailDetails.to} onChange={(e) => setEmailDetails({ ...emailDetails, to: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Subject" value={emailDetails.subject} onChange={(e) => setEmailDetails({ ...emailDetails, subject: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Body" value={emailDetails.body} onChange={(e) => setEmailDetails({ ...emailDetails, body: e.target.value })} />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "sms" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={5}>
                                            <TextField fullWidth label="Phone" value={smsDetails.phone} onChange={(e) => setSmsDetails({ ...smsDetails, phone: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={7}>
                                            <TextField fullWidth label="Message" value={smsDetails.message} onChange={(e) => setSmsDetails({ ...smsDetails, message: e.target.value })} />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "wifi" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Network Name (SSID)" value={wifiDetails.ssid} onChange={(e) => setWifiDetails({ ...wifiDetails, ssid: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Password" value={wifiDetails.password} onChange={(e) => setWifiDetails({ ...wifiDetails, password: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth sx={premiumInputSx}>
                                                <InputLabel>Encryption</InputLabel>
                                                <Select value={wifiDetails.encryption} label="Encryption" onChange={(e) => setWifiDetails({ ...wifiDetails, encryption: e.target.value })}>
                                                    <MenuItem value="WPA">WPA/WPA2</MenuItem>
                                                    <MenuItem value="WEP">WEP</MenuItem>
                                                    <MenuItem value="nopass">None</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel control={<Checkbox checked={wifiDetails.hidden} onChange={(e) => setWifiDetails({ ...wifiDetails, hidden: e.target.checked })} />} label="Hidden network" />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "location" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField fullWidth label="Latitude" value={locationDetails.latitude} onChange={(e) => setLocationDetails({ ...locationDetails, latitude: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField fullWidth label="Longitude" value={locationDetails.longitude} onChange={(e) => setLocationDetails({ ...locationDetails, longitude: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField fullWidth label="Place label" value={locationDetails.label} onChange={(e) => setLocationDetails({ ...locationDetails, label: e.target.value })} />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "event" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Event title" value={eventDetails.title} onChange={(e) => setEventDetails({ ...eventDetails, title: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Location" value={eventDetails.location} onChange={(e) => setEventDetails({ ...eventDetails, location: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth type="datetime-local" label="Start" InputLabelProps={{ shrink: true }} value={eventDetails.start} onChange={(e) => setEventDetails({ ...eventDetails, start: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth type="datetime-local" label="End" InputLabelProps={{ shrink: true }} value={eventDetails.end} onChange={(e) => setEventDetails({ ...eventDetails, end: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Description" value={eventDetails.description} onChange={(e) => setEventDetails({ ...eventDetails, description: e.target.value })} />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "chat" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={5}>
                                            <TextField fullWidth label="Phone / Handle" value={chatDetails.handle} onChange={(e) => setChatDetails({ ...chatDetails, handle: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={7}>
                                            <TextField fullWidth label="Prefilled Message" value={chatDetails.message} onChange={(e) => setChatDetails({ ...chatDetails, message: e.target.value })} />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "app" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="iOS deep-link" value={appDetails.ios} onChange={(e) => setAppDetails({ ...appDetails, ios: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Android deep-link" value={appDetails.android} onChange={(e) => setAppDetails({ ...appDetails, android: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Fallback web URL" value={appDetails.fallback} onChange={(e) => setAppDetails({ ...appDetails, fallback: e.target.value })} />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "social" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth sx={premiumInputSx}>
                                                <InputLabel>Service</InputLabel>
                                                <Select
                                                    value={socialDetails.service}
                                                    label="Service"
                                                    onChange={(e) => setSocialDetails({ ...socialDetails, service: e.target.value })}
                                                >
                                                    {SOCIAL_SERVICE_OPTIONS.map((item) => (
                                                        <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Handle / ID"
                                                value={socialDetails.handle}
                                                onChange={(e) => setSocialDetails({ ...socialDetails, handle: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Custom URL (optional override)"
                                                value={socialDetails.customUrl}
                                                onChange={(e) => setSocialDetails({ ...socialDetails, customUrl: e.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "bitcoin" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Wallet address"
                                                value={bitcoinDetails.wallet}
                                                onChange={(e) => setBitcoinDetails({ ...bitcoinDetails, wallet: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Amount (BTC)"
                                                value={bitcoinDetails.amount}
                                                onChange={(e) => setBitcoinDetails({ ...bitcoinDetails, amount: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Label"
                                                value={bitcoinDetails.label}
                                                onChange={(e) => setBitcoinDetails({ ...bitcoinDetails, label: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                label="Message"
                                                value={bitcoinDetails.message}
                                                onChange={(e) => setBitcoinDetails({ ...bitcoinDetails, message: e.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                ) : contentType === "facetime" && !id ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Phone or Email"
                                                value={faceTimeDetails.handle}
                                                onChange={(e) => setFaceTimeDetails({ ...faceTimeDetails, handle: e.target.value })}
                                            />
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <TextField
                                        fullWidth
                                        disabled={!!id}
                                        sx={premiumInputSx}
                                        label={contentType.charAt(0).toUpperCase() + contentType.slice(1)}
                                        value={id ? recordScanUrl : contentValue}
                                        onChange={(e) => setContentValue(e.target.value)}
                                    />
                                )}

                                {/* Quick presets */}
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2" sx={{ color: "#475569", fontWeight: 700 }}>
                                        Quick presets
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{
                                            "& .MuiChip-root": {
                                                maxWidth: "100%",
                                                width: { xs: "100%", sm: "auto" },
                                                justifyContent: { xs: "flex-start", sm: "center" },
                                            },
                                            "& .MuiChip-label": {
                                                display: "block",
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            },
                                        }}
                                    >
                                        <Chip
                                            icon={<LinkIcon />}
                                            label="URL • UTM campaign"
                                            onClick={() => {
                                                setContentType("url");
                                                setContentValue("https://yourbrand.com/launch?utm_source=qr&utm_medium=print&utm_campaign=spring");
                                            }}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                px: 0.5,
                                                color: "#1d4ed8",
                                                bgcolor: alpha("#60a5fa", 0.12),
                                                border: `1px solid ${alpha("#2563eb", 0.28)}`,
                                                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 6px 16px ${alpha("#3b82f6", 0.2)}` },
                                            }}
                                        />
                                        <Chip
                                            icon={<WifiIcon />}
                                            label="Wi-Fi • Guest network"
                                            onClick={() => {
                                                setContentType("wifi");
                                                setWifiDetails({ ssid: "CafeGuest", password: "Welcome2026!", encryption: "WPA", hidden: false });
                                            }}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                px: 0.5,
                                                color: "#0f766e",
                                                bgcolor: alpha("#2dd4bf", 0.16),
                                                border: `1px solid ${alpha("#14b8a6", 0.35)}`,
                                                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 6px 16px ${alpha("#14b8a6", 0.24)}` },
                                            }}
                                        />
                                        <Chip
                                            icon={<EventIcon />}
                                            label="Event • Launch party"
                                            onClick={() => {
                                                setContentType("event");
                                                setEventDetails({
                                                    title: "Spring Product Launch",
                                                    location: "Pier 48, SF",
                                                    start: "2026-04-12T18:00",
                                                    end: "2026-04-12T21:00",
                                                    description: "Live demo, giveaways, and creator meetups.",
                                                });
                                            }}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                px: 0.5,
                                                color: "#7c2d12",
                                                bgcolor: alpha("#fdba74", 0.18),
                                                border: `1px solid ${alpha("#f97316", 0.35)}`,
                                                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 6px 16px ${alpha("#fb923c", 0.22)}` },
                                            }}
                                        />
                                        <Chip
                                            icon={<StyleIcon />}
                                            label="Rounded • Navy"
                                            onClick={() => {
                                                setQrShape("rounded");
                                                setDotsColor("#0f172a");
                                                setBgColor("#ffffff");
                                                setBorderColor("#0f172a");
                                                setScanMeBorderColor("#0f172a");
                                                setScanMeFontColor("#ffffff");
                                            }}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                px: 0.5,
                                                color: "#312e81",
                                                bgcolor: alpha("#6366f1", 0.09),
                                                border: `1px solid ${alpha("#6366f1", 0.24)}`,
                                                backdropFilter: "blur(8px)",
                                                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 6px 16px ${alpha("#4f46e5", 0.2)}` },
                                            }}
                                        />
                                        <Chip
                                            icon={<StyleIcon />}
                                            label="Dots • Emerald"
                                            onClick={() => {
                                                setQrShape("dots");
                                                setDotsColor("#047857");
                                                setBgColor("#ffffff");
                                                setBorderColor("#047857");
                                                setScanMeBorderColor("#047857");
                                                setScanMeFontColor("#ffffff");
                                            }}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                px: 0.5,
                                                color: "#065f46",
                                                bgcolor: alpha("#10b981", 0.11),
                                                border: `1px solid ${alpha("#10b981", 0.32)}`,
                                                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 6px 16px ${alpha("#059669", 0.2)}` },
                                            }}
                                        />
                                        <Chip
                                            icon={<StyleIcon />}
                                            label="Square • Charcoal"
                                            onClick={() => {
                                                setQrShape("square");
                                                setDotsColor("#111827");
                                                setBgColor("#ffffff");
                                                setBorderColor("#111827");
                                                setScanMeBorderColor("#111827");
                                                setScanMeFontColor("#ffffff");
                                            }}
                                            sx={{
                                                fontWeight: 800,
                                                borderRadius: 999,
                                                px: 0.5,
                                                color: "#0f172a",
                                                bgcolor: alpha("#111827", 0.08),
                                                border: `1px solid ${alpha("#111827", 0.24)}`,
                                                "&:hover": { transform: "translateY(-1px)", boxShadow: `0 6px 16px ${alpha("#111827", 0.2)}` },
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                                <Divider sx={{ borderColor: alpha("#6366f1", 0.18) }} />
                                <Button
                                    component={RouterLink}
                                    to="/auth?mode=signup"
                                    fullWidth
                                    size="large"
                                    variant="contained"
                                    sx={{ py: 1.2, borderRadius: 2, fontWeight: 800, textTransform: "none" }}
                                >
                                    Make the Qr Dynamic
                                </Button>
                            </Stack>
                        </Card>

                        <Card sx={mobileSafeFormSx}>
                            <Stack spacing={1.5}>
                                <Typography variant="h6" sx={sectionTitleSx}>
                                    Appearance
                                </Typography>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2" sx={{ color: "#475569", fontWeight: 700 }}>
                                        Design themes
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{
                                            "& .MuiChip-root": {
                                                maxWidth: "100%",
                                                width: { xs: "100%", sm: "auto" },
                                                justifyContent: { xs: "flex-start", sm: "center" },
                                            },
                                            "& .MuiChip-label": {
                                                display: "block",
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            },
                                        }}
                                    >
                                        <Chip
                                            label="Aurora Gradient"
                                            onClick={() => {
                                                setQrShape("rounded");
                                                setUseDotsGradient(true);
                                                setDotsColor("#1d4ed8");
                                                setDotsSecondaryColor("#a855f7");
                                                setDotsGradientType("linear");
                                                setDotsGradientRotation(35);
                                                setUseBgGradient(true);
                                                setBgColor("#ffffff");
                                                setBgSecondaryColor("#eef2ff");
                                                setBorderColor("#4f46e5");
                                                setCornerSquareColor("#312e81");
                                                setCornerDotColor("#7c3aed");
                                                setFrameRadius(18);
                                            }}
                                            sx={{ fontWeight: 700, bgcolor: alpha("#6366f1", 0.12), border: `1px solid ${alpha("#4f46e5", 0.28)}` }}
                                        />
                                        <Chip
                                            label="Midnight Glass"
                                            onClick={() => {
                                                setQrShape("classy-rounded");
                                                setUseDotsGradient(false);
                                                setDotsColor("#e2e8f0");
                                                setUseBgGradient(false);
                                                setBgColor("#0f172a");
                                                setBorderColor("#334155");
                                                setCornerSquareColor("#cbd5e1");
                                                setCornerDotColor("#93c5fd");
                                                setScanMeBorderColor("#1e293b");
                                                setScanMeFontColor("#ffffff");
                                                setFrameRadius(16);
                                            }}
                                            sx={{ fontWeight: 700, bgcolor: alpha("#0f172a", 0.12), border: `1px solid ${alpha("#334155", 0.3)}` }}
                                        />
                                        <Chip
                                            label="Emerald Luxe"
                                            onClick={() => {
                                                setQrShape("dots");
                                                setUseDotsGradient(true);
                                                setDotsColor("#047857");
                                                setDotsSecondaryColor("#14b8a6");
                                                setDotsGradientType("radial");
                                                setUseBgGradient(false);
                                                setBgColor("#ffffff");
                                                setBorderColor("#047857");
                                                setCornerSquareColor("#065f46");
                                                setCornerDotColor("#059669");
                                                setScanMeBorderColor("#047857");
                                                setScanMeFontColor("#ecfdf5");
                                            }}
                                            sx={{ fontWeight: 700, bgcolor: alpha("#10b981", 0.12), border: `1px solid ${alpha("#059669", 0.3)}` }}
                                        />
                                    </Stack>
                                </Stack>

                                <Accordion disableGutters elevation={0} sx={accordionSx}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PaletteIcon fontSize="small" />
                                            <Typography>Colors & Border</Typography>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: alpha("#6366f1", 0.04), border: `1px solid ${alpha("#6366f1", 0.14)}` }}>
                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <ColorPickerField
                                                            label="Dots"
                                                            value={dotsColor}
                                                            onChange={(e) => setDotsColor(e.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <ColorPickerField
                                                            label="Background"
                                                            value={bgColor}
                                                            onChange={(e) => setBgColor(e.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={useDotsGradient} onChange={(e) => setUseDotsGradient(e.target.checked)} />}
                                                            label="Use dots gradient"
                                                        />
                                                    </Grid>
                                                    {useDotsGradient && (
                                                        <>
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <ColorPickerField
                                                                    label="Dots End"
                                                                    value={dotsSecondaryColor}
                                                                    onChange={(e) => setDotsSecondaryColor(e.target.value)}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    sx={premiumInputSx}
                                                                    fullWidth
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <FormControl fullWidth sx={premiumInputSx}>
                                                                    <InputLabel>Dots Gradient</InputLabel>
                                                                    <Select value={dotsGradientType} label="Dots Gradient" onChange={(e) => setDotsGradientType(e.target.value)}>
                                                                        <MenuItem value="linear">Linear</MenuItem>
                                                                        <MenuItem value="radial">Radial</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <TextField
                                                                    type="number"
                                                                    label="Dots Angle"
                                                                    value={dotsGradientRotation}
                                                                    onChange={(e) => setDotsGradientRotation(clamp(e.target.value, 0, 360))}
                                                                    InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
                                                                    sx={premiumInputSx}
                                                                    fullWidth
                                                                />
                                                            </Grid>
                                                        </>
                                                    )}
                                                    <Grid item xs={12} md={4}>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={useBgGradient} onChange={(e) => setUseBgGradient(e.target.checked)} />}
                                                            label="Use background gradient"
                                                        />
                                                    </Grid>
                                                    {useBgGradient && (
                                                        <>
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <ColorPickerField
                                                                    label="BG End"
                                                                    value={bgSecondaryColor}
                                                                    onChange={(e) => setBgSecondaryColor(e.target.value)}
                                                                    InputLabelProps={{ shrink: true }}
                                                                    sx={premiumInputSx}
                                                                    fullWidth
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <FormControl fullWidth sx={premiumInputSx}>
                                                                    <InputLabel>BG Gradient</InputLabel>
                                                                    <Select value={bgGradientType} label="BG Gradient" onChange={(e) => setBgGradientType(e.target.value)}>
                                                                        <MenuItem value="linear">Linear</MenuItem>
                                                                        <MenuItem value="radial">Radial</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} md={4}>
                                                                <TextField
                                                                    type="number"
                                                                    label="BG Angle"
                                                                    value={bgGradientRotation}
                                                                    onChange={(e) => setBgGradientRotation(clamp(e.target.value, 0, 360))}
                                                                    InputProps={{ endAdornment: <InputAdornment position="end">°</InputAdornment> }}
                                                                    sx={premiumInputSx}
                                                                    fullWidth
                                                                />
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Box>

                                            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: alpha("#0f172a", 0.03), border: `1px solid ${alpha("#6366f1", 0.12)}` }}>
                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <ColorPickerField
                                                            label="Border"
                                                            value={borderColor}
                                                            onChange={(e) => setBorderColor(e.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            type="number"
                                                            label="Frame Radius"
                                                            value={frameRadius}
                                                            onChange={(e) => setFrameRadius(clamp(e.target.value, 0, 40))}
                                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            type="number"
                                                            label="Frame Shadow"
                                                            value={frameShadow}
                                                            onChange={(e) => setFrameShadow(clamp(e.target.value, 0, 60))}
                                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            type="number"
                                                            label="Border Width"
                                                            value={borderWidth}
                                                            onChange={(e) => setBorderWidth(clamp(e.target.value, 0, 24))}
                                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            type="number"
                                                            label="H Margin"
                                                            value={marginH}
                                                            onChange={(e) => setMarginH(clamp(e.target.value, 0, 64))}
                                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={4}>
                                                        <TextField
                                                            type="number"
                                                            label="V Margin"
                                                            value={marginV}
                                                            onChange={(e) => setMarginV(clamp(e.target.value, 0, 64))}
                                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                                            sx={premiumInputSx}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion disableGutters elevation={0} sx={accordionSx}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <StyleIcon fontSize="small" />
                                            <Typography>Shape</Typography>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <ToggleButtonGroup
                                            value={qrShape}
                                            exclusive
                                            onChange={(_e, val) => val && setQrShape(val)}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: isSmDown ? "1fr" : "repeat(3, minmax(0, 1fr))",
                                                width: "100%",
                                                "& .MuiToggleButton-root": {
                                                    fontWeight: 700,
                                                    py: 1.2,
                                                    borderColor: alpha("#6366f1", 0.18),
                                                    textTransform: "none",
                                                    "&.Mui-selected": {
                                                        color: "primary.main",
                                                        bgcolor: alpha("#6366f1", 0.12),
                                                    },
                                                },
                                            }}
                                        >
                                            <ToggleButton value="square">Square</ToggleButton>
                                            <ToggleButton value="dots">Dots</ToggleButton>
                                            <ToggleButton value="rounded">Rounded</ToggleButton>
                                            <ToggleButton value="classy">Classy</ToggleButton>
                                            <ToggleButton value="classy-rounded">Classy Rounded</ToggleButton>
                                            <ToggleButton value="extra-rounded">Extra Rounded</ToggleButton>
                                        </ToggleButtonGroup>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion disableGutters elevation={0} sx={accordionSx}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ImageIcon fontSize="small" />
                                            <Typography>Corners, Logo & “Scan Me”</Typography>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2.25}>
                                            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: alpha("#6366f1", 0.05), border: `1px solid ${alpha("#6366f1", 0.14)}` }}>
                                                <Typography variant="overline" sx={{ display: "block", letterSpacing: 1.2, color: "text.secondary", mb: 1 }}>
                                                    Corners
                                                </Typography>
                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth sx={premiumInputSx}><InputLabel>Corner Square</InputLabel><Select value={cornerSquareType} label="Corner Square" onChange={(e) => setCornerSquareType(e.target.value)}><MenuItem value="square">Square</MenuItem><MenuItem value="dot">Dot</MenuItem><MenuItem value="extra-rounded">Extra Rounded</MenuItem></Select></FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth sx={premiumInputSx}><InputLabel>Corner Dot</InputLabel><Select value={cornerDotType} label="Corner Dot" onChange={(e) => setCornerDotType(e.target.value)}><MenuItem value="square">Square</MenuItem><MenuItem value="dot">Dot</MenuItem></Select></FormControl>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}><ColorPickerField label="Square Color" value={cornerSquareColor} onChange={(e) => setCornerSquareColor(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
                                                    <Grid item xs={6} sm={3}><ColorPickerField label="Dot Color" value={cornerDotColor} onChange={(e) => setCornerDotColor(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
                                                </Grid>
                                            </Box>

                                            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: alpha("#10b981", 0.05), border: `1px solid ${alpha("#10b981", 0.18)}` }}>
                                                <Typography variant="overline" sx={{ display: "block", letterSpacing: 1.2, color: "text.secondary", mb: 1 }}>Logo</Typography>
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
                                                    <Button variant="outlined" component="label" sx={{ fontWeight: 800, borderRadius: 2.5, px: 2.25, py: 1 }}>Upload Logo<input type="file" accept="image/*" hidden onChange={handleLogoUpload} /></Button>
                                                    {logoData && <Chip size="small" color="success" label="Logo attached" sx={{ fontWeight: 700 }} />}
                                                </Stack>
                                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                                    <Grid item xs={12} sm={6} md={4}><TextField sx={premiumInputSx} fullWidth type="number" label="Logo Size" value={logoSize} onChange={(e) => setLogoSize(clamp(e.target.value, 0.1, 0.8))} inputProps={{ step: 0.1, min: 0.1, max: 0.8 }} InputProps={{ endAdornment: <InputAdornment position="end">0-1</InputAdornment> }} /></Grid>
                                                    <Grid item xs={12} sm={6} md={4}><TextField sx={premiumInputSx} fullWidth type="number" label="Logo Margin" value={logoMargin} onChange={(e) => setLogoMargin(clamp(e.target.value, 0, 24))} InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }} /></Grid>
                                                    <Grid item xs={12} md={4}><FormControlLabel sx={{ m: 0, height: "100%", alignItems: "center" }} control={<Checkbox checked={hideLogoBgDots} onChange={(e) => setHideLogoBgDots(e.target.checked)} />} label="Hide dots behind logo" /></Grid>
                                                </Grid>
                                            </Box>

                                            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: alpha("#a855f7", 0.05), border: `1px solid ${alpha("#a855f7", 0.18)}` }}>
                                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.25}>
                                                    <FormControlLabel control={<Checkbox checked={showScanMe} onChange={(e) => setShowScanMe(e.target.checked)} />} label="Show “SCAN ME”" sx={{ m: 0 }} />
                                                    <ToggleButtonGroup value={scanMePosition} exclusive onChange={(_e, v) => v && setScanMePosition(v)} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'uppercase', fontWeight: 700, px: 2, borderRadius: 2 } }}><ToggleButton value="top">Top</ToggleButton><ToggleButton value="bottom">Bottom</ToggleButton></ToggleButtonGroup>
                                                </Stack>
                                                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                                                    <Grid item xs={12} md={6}><TextField sx={premiumInputSx} fullWidth label="Custom Text" value={ScanMe} onChange={(e) => setScanMe(e.target.value)} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><TextField sx={premiumInputSx} fullWidth type="number" label="Font Size" value={scanMeFontSize} onChange={(e) => setScanMeFontSize(clamp(e.target.value, 10, 42))} InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><TextField sx={premiumInputSx} fullWidth type="number" label="Weight" value={scanMeFontWeight} onChange={(e) => setScanMeFontWeight(clamp(e.target.value, 400, 900))} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><TextField sx={premiumInputSx} fullWidth type="number" label="Spacing" value={scanMeLetterSpacing} onChange={(e) => setScanMeLetterSpacing(clamp(e.target.value, 0, 8))} InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><ColorPickerField sx={premiumInputSx} fullWidth label="Text Color" value={scanMeFontColor} onChange={(e) => setScanMeFontColor(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                                                    <Grid item xs={12} md={6}><ColorPickerField sx={premiumInputSx} fullWidth label="Bar Color" value={scanMeBorderColor} onChange={(e) => setScanMeBorderColor(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                                                </Grid>
                                            </Box>
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>

                                {id && (
                                    <Button
                                        fullWidth
                                        startIcon={<SaveIcon />}
                                        variant="contained"
                                        onClick={handleSaveSettings}
                                        sx={{
                                            py: 1.25,
                                            fontWeight: 900,
                                            borderRadius: 999,
                                            backgroundImage: "linear-gradient(135deg,#6366f1 0%,#a855f7 55%,#ec4899 100%)",
                                            boxShadow: "0 14px 26px rgba(147, 51, 234, 0.34)",
                                        }}
                                    >
                                        Update Settings
                                    </Button>
                                )}
                            </Stack>
                        </Card>
                    </Stack>
                </Grid>

                {/* RIGHT: Preview & Downloads */}
                <Grid
                    item
                    xs={12}
                    md={5}
                    sx={{
                        minWidth: 0,
                        maxWidth: "100%",
                        width: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Card
                        sx={{
                            position: isMdUp ? "sticky" : "static",
                            top: 24,
                            background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(239,244,255,0.95) 100%)",
                            overflow: "hidden",
                        }}
                    >
                        <Stack
                            spacing={2.5}
                            alignItems="center"
                            textAlign="center"
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                minWidth: 0,
                                boxSizing: "border-box",
                                px: { xs: 1, sm: 3 },
                                pt: { xs: 2, sm: 3 },
                                pb: 3,
                                overflow: "hidden",
                            }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                Preview
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                                Your QR updates live with every style and content change.
                            </Typography>
                            <Chip
                                label="Optimized for mobile scanning"
                                sx={{
                                    width: "100%",
                                    maxWidth: 320,
                                    height: "auto",
                                    fontWeight: 700,
                                    color: "#312e81",
                                    bgcolor: alpha("#818cf8", 0.14),
                                    border: `1px solid ${alpha("#6366f1", 0.28)}`,
                                    "& .MuiChip-label": {
                                        display: "block",
                                        whiteSpace: "normal",
                                        textAlign: "center",
                                        overflowWrap: "anywhere",
                                        py: 0.75,
                                    },
                                }}
                            />
                            {/* Whole export area (bar + QR) */}
                            <Box
                                ref={downloadRef}
                                sx={{
                                    width: safePreviewWidth,
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    mx: "auto",
                                    boxSizing: "border-box",
                                    border: `${borderWidth}px solid ${borderColor}`,
                                    borderRadius: `${frameRadius}px`,
                                    backgroundColor: frameFillColor,
                                    boxShadow: `0 28px 50px ${alpha(borderColor, Math.min(0.6, frameShadow / 100))}`,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: scanMePosition === "bottom" ? "column-reverse" : "column",
                                }}
                            >
                                {/* Scan-Me bar */}
                                {showScanMe && (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            bgcolor: scanMeBorderColor,
                                            height: `${Math.round(scanMeFontSize + 20)}px`,
                                            overflow: "hidden",
                                        }}
                                    >
                                        {/* <Typography
                                            component="span"
                                            sx={{
                                                color: scanMeFontColor,
                                                fontSize: scanMeFontSize,
                                                fontWeight: scanMeFontWeight,
                                                letterSpacing: scanMeLetterSpacing,
                                                display: "block",
                                                width: "100%",
                                                textAlign: "center",
                                                lineHeight: `${Math.round(scanMeFontSize + 20)}px`,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {ScanMe}
                                        </Typography> */}

                                        <Typography
                                            component="span"
                                            data-export-scan-text="true"
                                            sx={{
                                                color: scanMeFontColor,
                                                fontSize: scanMeFontSize,
                                                fontWeight: scanMeFontWeight,
                                                letterSpacing: scanMeLetterSpacing,
                                                display: "block",
                                                width: "100%",
                                                textAlign: "center",
                                                lineHeight: `${Math.round(scanMeFontSize + 20)}px`,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {ScanMe}
                                        </Typography>

                                    </Box>

                                )}

                                {/* QR block */}
                                <Box
                                    ref={previewRef}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                        minHeight: { xs: "auto", sm: qrFrameHeight },
                                        p: `${marginV}px ${marginH}px`,
                                        boxSizing: "border-box",
                                        bgcolor: bgColor,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexGrow: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `min(100%, ${qrSize}px)`,
                                            maxWidth: "100%",
                                            aspectRatio: "1 / 1",
                                            position: "relative",
                                            flexShrink: 1,
                                            mx: "auto",
                                        }}
                                    >
                                        <Box
                                            ref={qrCodeRef}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                "& > *": {
                                                    display: "block",
                                                    margin: "0 auto",
                                                    maxWidth: "100%",
                                                },
                                                "& canvas, & svg": {
                                                    width: "100% !important",
                                                    height: "100% !important",
                                                    display: "block",
                                                },
                                            }}
                                        />
                                        {logoData && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    width: `${Math.round(logoSize * 100)}%`,
                                                    maxWidth: "80%",
                                                    transform: "translate(-50%, -50%)",
                                                    p: `${logoMargin}px`,
                                                    borderRadius: 1.5,
                                                    bgcolor: hideLogoBgDots ? bgColor : "transparent",
                                                    boxSizing: "content-box",
                                                    pointerEvents: "none",
                                                    lineHeight: 0,
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={logoData}
                                                    alt="QR logo"
                                                    sx={{ width: "100%", height: "auto", display: "block" }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Download buttons */}
                            <Box className="download-button-grid" aria-label="Download QR code formats">
                                {["png", "jpeg", "svg", "pdf"].map((ext) => (
                                    <Button
                                        key={ext}
                                        variant="contained"
                                        className={`download-format-btn download-format-btn--${ext}`}
                                        onClick={() => handleDownload(ext)}
                                    >
                                        <Box className="download-format-icon" aria-hidden="true">
                                            <DownloadIcon />
                                        </Box>
                                        <Box component="span" className="download-format-version">
                                            {ext.toUpperCase()}
                                        </Box>
                                    </Button>
                                ))}
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}
